'use client';

// The modal title row, presentation-aware (BL-1998, BL-2108, BL-2291): the
// shared white `PanelHeader` on the card (BL-2692), and on the phone sheet the
// `.ui-modal__head` row. Lifted out of New auction's `CreateAuctionHead` when Add lot became
// the second phone-sheet form, so the sheet header has one implementation
// (ADR 044 — Must Consolidate).

import type { ReactNode } from 'react';
import { useModalPresentation } from './modal-presentation';
import { useSheetIntro } from './sheet-intro-context';
import PanelHeader, { type PanelHeaderWidth } from './PanelHeader';

/** Props for {@link ModalHead}. */
export interface ModalHeadProps {
    /** The heading. */
    title: string;
    /**
     * A second line under the title on the phone sheet only — e.g. the auction a
     * new lot lands in. One line; truncates. A sheet with a `SheetIntro` shows
     * none: the intro's description carries it (BL-2402).
     */
    sheetSubtitle?: ReactNode;
    /** The card's help line under the title. The sheet hides it (the caller may move it into the body). */
    cardHelp?: ReactNode;
    /** Closes the modal: the ✕ on the card, the leading text button on the sheet. */
    onClose: () => void;
    /** Automation handle on the close control (the ✕ on the card, the leading button on the sheet). */
    closeTestId?: string;
    /**
     * What the sheet's leading button says: `cancel` (default) on a form with a
     * bottom bar, `done` on a sheet with nothing to save (toggles that save on
     * flip). Ignored on the card.
     */
    sheetLeading?: 'cancel' | 'done';
    /** Locks the close control while the caller's write is in flight, so the sheet can't be dismissed under it. */
    closeDisabled?: boolean;
    /**
     * The card's width class, which picks the title step (BL-2915). The
     * `.ui-modal` card is 520px, so `narrow` (16/24) by default; a caller on the
     * 720px `.ui-modal--lg` card passes `wide` (20/28). Ignored on the sheet.
     */
    cardWidth?: PanelHeaderWidth;
}

const SHEET_LEADING_LABEL = { cancel: 'Cancel', done: 'Done' } as const;

/**
 * The modal's title row. The card is the shared white `PanelHeader` (BL-2692):
 * the title, an optional help line and the ✕. The phone sheet shows a "Cancel" (or "Done") text button at the left, the
 * title centered in a white 56px bar with an optional subtitle under it, and no
 * ✕ (BL-2291). On a sheet with a `SheetIntro` the bar's title stays empty until
 * the intro scrolls away (`data-intro`, ADR 063 — Type Scale Amendment 5,
 * BL-2402). A hidden copy of the leading label sits on the right so the
 * title keeps the same safe zone on both sides (`.ui-modal--sheet` in
 * settings-v2.css owns that geometry).
 *
 * @param props - {@link ModalHeadProps}
 * @returns The shared `PanelHeader` on the card; the `.ui-modal__head` row on the phone sheet.
 */
export default function ModalHead({
    title,
    sheetSubtitle,
    cardHelp,
    onClose,
    closeTestId,
    sheetLeading = 'cancel',
    closeDisabled = false,
    cardWidth = 'narrow',
}: ModalHeadProps) {
    const isSheet = useModalPresentation() === 'sheet';
    const intro = useSheetIntro().state;
    const hasIntro = isSheet && intro !== 'none';
    const leadingLabel = SHEET_LEADING_LABEL[sheetLeading];
    // The card draws the one white header every admin modal shares (BL-2692).
    if (!isSheet) {
        return (
            <PanelHeader
                density="form"
                width={cardWidth}
                title={title}
                subtitle={cardHelp}
                onClose={onClose}
                closeTestId={closeTestId}
                closeDisabled={closeDisabled}
            />
        );
    }
    return (
        <div className="ui-modal__head" data-intro={hasIntro ? intro : undefined}>
            {/* Back out from where a phone user looks first; the bar below
                holds only the real actions (BL-2291, replacing BUG-1860's ✕). */}
            <button type="button" className="ui-modal__lead" onClick={onClose} disabled={closeDisabled} data-testid={closeTestId}>
                {leadingLabel}
            </button>
            {/* While the intro shows the title, the faded-out bar title is not read a second time. */}
            <div aria-hidden={intro === 'hidden' ? true : undefined}>
                {/* An inline size rather than a class: `.ds-h2` is `--type-title-lg`
                    (24) and settings-v2.css loads after Tailwind, so a class would
                    lose the cascade. The sheet overrides it to `--type-title-sm`. */}
                <h2 className="ds-h2" style={{ fontSize: 'var(--type-title)' }}>{title}</h2>
                {/* One line, ellipsis — a sheet header never grows a third line.
                    gray-500 on white is 4.83:1 (ADR 051 — Contrast Floor). */}
                {sheetSubtitle != null && !hasIntro ? <span className="block truncate text-body text-gray-500">{sheetSubtitle}</span> : null}
            </div>
            <span className="ui-modal__lead-mirror" aria-hidden="true">
                {leadingLabel}
            </span>
        </div>
    );
}
